import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { ArrowLeft } from "lucide-react";
import ImageUploader from "./ImageUploader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminEventForm() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const isEdit = !!eventId;

  const [form, setForm] = useState({
    brand: "",
    badge: "",
    badgeColor: "",
    subtitle: "",
    image: "",
    objectPosition: "center center",
    status: "draft",
    eventEndTime: "",
    category: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    fetch(`${API_URL}/api/admin/events/${eventId}`, {
      headers: { Authorization: `Bearer ${admin.token}` },
    })
      .then((r) => r.json())
      .then((data) =>
        setForm({
          brand: data.brand || "",
          badge: data.badge || "",
          badgeColor: data.badgeColor || "",
          subtitle: data.subtitle || "",
          image: data.image || "",
          objectPosition: data.objectPosition || "center center",
          status: data.status || "draft",
          eventEndTime: data.eventEndTime ? data.eventEndTime.slice(0, 16) : "",
          category: data.category || "",
        })
      )
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isEdit, eventId, admin.token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = isEdit
        ? `${API_URL}/api/admin/events/${eventId}`
        : `${API_URL}/api/admin/events`;
      const method = isEdit ? "PUT" : "POST";
      const body = {
        brand: form.brand,
        badge: form.badge || null,
        badgeColor: form.badgeColor || null,
        subtitle: form.subtitle,
        image: form.image,
        objectPosition: form.objectPosition,
        status: form.status,
        eventEndTime: form.eventEndTime ? new Date(form.eventEndTime).toISOString() : null,
        category: form.category || null,
      };
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Save failed");
      navigate("/admin/events");
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#333] border-t-[#D8C08F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-event-form" className="max-w-2xl">
      <button
        onClick={() => navigate("/admin/events")}
        className="flex items-center gap-2 text-[#888] hover:text-white text-sm font-sans mb-6 transition-colors"
        data-testid="back-to-events"
      >
        <ArrowLeft size={16} />
        Back to Events
      </button>

      <h1 className="text-white text-2xl font-sans font-semibold mb-6">
        {isEdit ? "Edit Event" : "Create Event"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Brand */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Brand / Event Title *
          </label>
          <input
            type="text"
            value={form.brand}
            onChange={(e) => update("brand", e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
            placeholder="e.g. KARL LAGERFELD"
            required
            data-testid="input-brand"
          />
        </div>

        {/* Badge */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Badge Text
          </label>
          <input
            type="text"
            value={form.badge}
            onChange={(e) => update("badge", e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
            placeholder="e.g. FLASH SALE (leave empty for no badge)"
            data-testid="input-badge"
          />
        </div>

        {/* Badge Color */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Badge Color
          </label>
          <select
            value={form.badgeColor}
            onChange={(e) => update("badgeColor", e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
            data-testid="input-badgeColor"
          >
            <option value="">Default (Olive)</option>
            <option value="burgundy">Burgundy / Red</option>
            <option value="olive">Olive</option>
            <option value="champagne">Beige / Champagne</option>
            <option value="black">Black</option>
            <option value="white">White Translucent</option>
            <option value="gold">Gold / Bronze</option>
          </select>
        </div>

        {/* Subtitle */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Subtitle (optional descriptive text)
          </label>
          <input
            type="text"
            value={form.subtitle}
            onChange={(e) => update("subtitle", e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
            placeholder="e.g. 1 нед. ост. (optional label, not the timer)"
            data-testid="input-subtitle"
          />
        </div>

        {/* Event End Time */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Event End Time *
          </label>
          <input
            type="datetime-local"
            value={form.eventEndTime}
            onChange={(e) => update("eventEndTime", e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors [color-scheme:dark]"
            required
            data-testid="input-eventEndTime"
          />
          <p className="text-[#555] text-xs font-sans mt-1.5">
            Countdown timer on event cards is calculated from this time
          </p>
        </div>

        {/* Category */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Category
          </label>
          <select
            value={form.category}
            onChange={(e) => update("category", e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
            data-testid="input-category"
          >
            <option value="">No category (shows in all)</option>
            <option value="clothing">Одежда (Clothing)</option>
            <option value="shoes">Обувь (Shoes)</option>
            <option value="bags">Сумки (Bags)</option>
            <option value="watches">Часы (Watches)</option>
            <option value="accessories">Аксессуары (Accessories)</option>
            <option value="mens">Мужское (Men's)</option>
          </select>
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Event Image
          </label>
          <ImageUploader
            currentImage={form.image}
            onImageChange={(url) => update("image", url)}
          />
        </div>

        {/* Object Position */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Image Position
          </label>
          <select
            value={form.objectPosition}
            onChange={(e) => update("objectPosition", e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
            data-testid="input-objectPosition"
          >
            <option value="center center">Center</option>
            <option value="center 30%">Top Third</option>
            <option value="center 55%">Lower Center</option>
            <option value="center top">Top</option>
            <option value="center bottom">Bottom</option>
          </select>
        </div>

        {/* Status */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Status
          </label>
          <select
            value={form.status}
            onChange={(e) => update("status", e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
            data-testid="input-status"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Submit */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#D8C08F] text-black font-semibold px-6 py-3 rounded-lg text-sm font-sans hover:bg-[#c5ad7d] transition-colors disabled:opacity-50"
            data-testid="save-event-button"
          >
            {saving ? "Saving..." : isEdit ? "Update Event" : "Create Event"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/events")}
            className="bg-[#1a1a1a] border border-[#2a2a2a] text-[#888] font-medium px-6 py-3 rounded-lg text-sm font-sans hover:text-white transition-colors"
            data-testid="cancel-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
